/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / erreurs axios comme U3203 */
/* eslint-disable react-hooks/exhaustive-deps -- effets alignés chargement id / handlers stables */
import React, { useEffect, useReducer, useState } from 'react';
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import U2000DefaultForm from '../../u2000Core/components/formControls/U2000DefaultForm';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000GetHandlers } from '../../u2000Core/hooks/handler/U2000UseGetHandlers';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { useU2000SearchHandlers } from '../../u2000Core/hooks/handler/U2000UseSearchHandlers';
import type { U2000SearchPayload, U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import { useU2000SaveHandlers } from '../../u2000Core/hooks/handler/U2000UseSaveHandlers';
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from '../../u2000Core/reducer/formReducer/U2000FormState';
import { U2000LUStateEnumNextAction } from '../../u2000Core/reducer/formReducer/U2000LUState';
import { useU2000DeleteHandlers } from '../../u2000Core/hooks/handler/U2000UseDeleteHandlers';
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from '../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { NS, U3206ROLES } from '../U3206';
import U3206AiUserGroupHeader from './form/U3206AiUserGroupHeader';
import { U3206AiUserGroupFormToolBar } from './form/U3206AiUserGroupFormToolBar';
import U3206AiUserGroupDeleteDlg from './common/U3206AiUserGroupDeleteDlg';
import { useU3206Context } from '../U3206Context';
import U2000AuditPanelComponent from '../../u2000Core/components/audit/U2000AuditPanelComponent';
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  createEmptyU3206FormRow,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  luHandleAppiError,
  normalizeU3206AiUserGroupFromApi,
  type U3206AiUserGroupFormRow,
  type U3206AiUserGroupSearchRow,
} from '../U3206Service';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000Tab from '../../u2000Core/components/tab/U2000Tab';
import U3206ProvidersTab from './form/u3206AiUserGroup/U3206ProvidersTab';
import U3206MissionsTab from './form/u3206AiUserGroup/U3206MissionsTab';
import U3206UsersTab from './form/u3206AiUserGroup/U3206UsersTab';
import U3206TokenUsageTab from './form/u3206AiUserGroup/U3206TokenUsageTab';
import U3206DataAccessTab from './form/u3206AiUserGroup/U3206DataAccessTab';

const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;

export interface U3206AiUserGroupFormExtendedHandlers extends U2000FormStateHandlersReturn<U3206AiUserGroupFormRow> {
  addNewGroup: () => void;
  modifyGroup: () => void;
  deleteGroup: () => Promise<void>;
}

const U3206AiUserGroupForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3206Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listTitle = t('title', { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U3206AiUserGroupFormRow) => {
    const result: Record<string, string> = {};
    const isAdd = !row.id;

    if (isAdd) {
      const code = row.code?.trim().toUpperCase() ?? '';
      if (!code) {
        result['code'] = t('errCodeRequired', { ns: NS });
      } else if (code.length > 64) {
        result['code'] = t('errCodeMax', { ns: NS });
      } else if (!CODE_PATTERN.test(code)) {
        result['code'] = t('errCodePattern', { ns: NS });
      }
    }

    const name = row.name?.trim() ?? '';
    if (!name) {
      result['name'] = t('errNameRequired', { ns: NS });
    } else if (name.length > 255) {
      result['name'] = t('errNameMax', { ns: NS });
    }

    return result;
  };

  const u3206BL: U2000FormSBusinessHandlers<U3206AiUserGroupFormRow> = {
    newRecord: () => createEmptyU3206FormRow(),
    validateForm,
    saveForme: async (row: U3206AiUserGroupFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3206FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },
    cancelForm: () => {
      const old = u3206FormExtended.getOldData() as U3206AiUserGroupFormRow | null;
      if (old) {
        u3206FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === '/U3206') {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },
    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },
    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onLeaveForm: () => {
      navigate('/U3206');
    },
  };

  const u3206FormHandlers = useU2000FormStateHandlers<U3206AiUserGroupFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3206BL,
  });

  const u3206FormExtended: U3206AiUserGroupFormExtendedHandlers = {
    ...u3206FormHandlers,
    addNewGroup: () => {
      u3206FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    },
    modifyGroup: () => {
      u3206FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deleteGroup: async () => {
      await handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3206AiUserGroupSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => doSearch(u2000AxiosAuthenticated, searchPayload),
    onSuccess: (searchResult: U2000SearchResult<U3206AiUserGroupSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3206AiUserGroupFormRow>({
    doGetFn: (rowId: string) => doGetData(u2000AxiosAuthenticated, rowId),
    onSuccess: (row: U3206AiUserGroupFormRow) => {
      const normalized = normalizeU3206AiUserGroupFromApi(row);
      gridStateHandlers.setDataById(normalized.id, normalized as unknown as U3206AiUserGroupSearchRow);
      u3206FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.code}${normalized.name ? ` — ${normalized.name}` : ''}`;
      setLogicalUnitBreadcrumb([
        { href: '/U3206', label: listTitle },
        { label: crumbLabel },
      ]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3206FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3206AiUserGroupFormRow>({
    doSaveFn: (row: U3206AiUserGroupFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3206AiUserGroupFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeU3206AiUserGroupFromApi(row);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), normalized as unknown as U3206AiUserGroupSearchRow]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, normalized as unknown as U3206AiUserGroupSearchRow);
      }
      u3206FormExtended.afterSaveData(normalized);
      u3206FormExtended.setData(normalized);
      toast.success(t('msgLogicalUnitSaved', { ns: NS }));

      if (
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD ||
        luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY
      ) {
        if (luStateHandlers.getActionOriginFrom() === '/U3206') {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3206FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3206FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => {
      const data = u3206FormExtended.getData();
      return data ? [data as unknown as U3206AiUserGroupSearchRow] : [];
    },
    doDeleteFn: async (rowId) => {
      const data = u3206FormExtended.getData();
      if (!data) return;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(data));
    },
    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t('success0003', { key: (del as U3206AiUserGroupSearchRow).code, ns: NS }), t('successTitle', { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(
        currentIdx,
        newData,
        totalRows,
      );
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate('/U3206', { replace: true });
      }
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    if (id === 'new') {
      u3206FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
      u3206FormExtended.setData(createEmptyU3206FormRow());
    } else {
      handleGet.getData(id);
    }
  }, [id]);

  if (u3206FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    {
      id: 'tabProviders',
      label: t('tabProviders', { ns: NS }),
      content: <U3206ProvidersTab formStateHandlers={u3206FormExtended as any} t={t} />,
    },
    {
      id: 'tabMissions',
      label: t('tabMissions', { ns: NS }),
      content: <U3206MissionsTab formStateHandlers={u3206FormExtended as any} t={t} />,
    },
    {
      id: 'tabUsers',
      label: t('tabUsers', { ns: NS }),
      content: <U3206UsersTab formStateHandlers={u3206FormExtended as any} t={t} />,
    },
    {
      id: 'tabData',
      label: t('tabData', { ns: NS }),
      content: <U3206DataAccessTab formStateHandlers={u3206FormExtended as any} t={t} />,
    },
    {
      id: 'tabTokenUsage',
      label: t('tabTokenUsage', { ns: NS }),
      content: (
        <U3206TokenUsageTab
          formStateHandlers={u3206FormExtended as any}
          t={t}
          isActive={(activeTab ?? 'tabProviders') === 'tabTokenUsage'}
        />
      ),
    },
    hasRole(U3206ROLES.AUDITS) && {
      id: 'tabAudits',
      label: t('tabAudits', { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3206FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3206AiUserGroupAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      <U3206AiUserGroupDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3206AiUserGroupFormToolBar formStateHandlers={u3206FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3206FormExtended as any}
      >
        <U3206AiUserGroupHeader formStateHandlers={u3206FormExtended as any} t={t} />

        <U2000Tab
          className="mt-4"
          tabs={formTabs}
          defaultTab="tabProviders"
          activeTab={activeTab ?? 'tabProviders'}
          setActiveTab={(tab) => setActiveTab(typeof tab === 'string' ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3206AiUserGroupForm;
